import { Stack, Typography } from '@mui/material'
import React from 'react'
import { useBoolean } from 'react-use'

import agent from 'agent'
import Button from 'components/common/Button'
import ConfirmationModal from 'components/common/ConfirmationModal'
import TextArea from 'components/common/TextArea'
import useAuth from 'hooks/use-auth'
import toast from 'lib/toast'
import { track } from 'track'
import { messageFromResponseError } from 'utils/strings'

const HaltAllConfirmation: React.FC<{
  open: boolean
  onCancel(): void
}> = ({ open, onCancel }) => {
  const { currentTeamId } = useAuth()
  const [reason, setReason] = React.useState('')
  const [isLoading, setIsLoading] = useBoolean(false)

  return (
    <ConfirmationModal
      confirmText="Confirm"
      css={{ textAlign: 'left' }}
      disabled={isLoading}
      loading={isLoading}
      message={
        <Stack alignItems="start" spacing={2}>
          <Typography>
            This action will halt all tests within your company that are
            currently running.
          </Typography>
          <TextArea
            data-test-input="reason"
            name="reason"
            placeholder="Optional Reason"
            value={reason}
            onChange={({ target: { value } }) => setReason(value)}
          />
        </Stack>
      }
      open={open}
      title="Halt All Tests"
      onCancel={onCancel}
      onConfirm={async () => {
        setIsLoading(true)
        try {
          await agent.post(`/halts?teamId=${currentTeamId}`, { reason })

          toast({
            text: 'Test successfully halted.',
            type: 'success'
          })
          setIsLoading(false)
          const trackingOptions = { attackIds: 'all' }

          track('HaltAllAttacks', trackingOptions)
        } catch (e) {
          toast({
            type: 'error',
            text: messageFromResponseError(
              e,
              'There was an issue halting all tests. Please try again later.'
            )
          })
          setIsLoading(false)
        }
        onCancel()
      }}
    />
  )
}

export default function HaltAllAttacksButton() {
  const [isHaltConfirmationOpen, setIsHaltConfirmationOpen] = useBoolean(false)

  return (
    <>
      <HaltAllConfirmation
        open={isHaltConfirmationOpen}
        onCancel={() => setIsHaltConfirmationOpen(false)}
      />
      <Button
        data-test-halt-all-button
        destructive
        css={{
          whiteSpace: 'nowrap'
        }}
        onClick={() => setIsHaltConfirmationOpen(true)}
      >
        Halt All Tests
      </Button>
    </>
  )
}
